import React, { useState } from 'react';
import {
  Shield, Plane, FileWarning, HardHat,
  Users, AlertTriangle, ChevronRight, ShieldAlert, ShieldCheck, CheckCircle, X,
} from 'lucide-react';
import { StatusBadge } from '../UI/StatusBadge';
import { CountBadge } from '../UI/CountBadge';
import Portal from '../UI/Portal';
import { Link } from 'react-router-dom';
import { formatDate, formatExpiryUrgencyLabel, parseLocalDate } from '../../utils/dateUtils';

const TAB_CONFIG = [
  { key: 'work_pass', label: 'Work Pass', icon: Shield },
  { key: 'passport', label: 'Passport', icon: Plane },
  { key: 'security_bond', label: 'Security Bond', icon: FileWarning },
  { key: 'safety_certs', label: 'Safety Certs', icon: HardHat },
];

const formatExpiryDate = (value) => {
  if (!value) return null;
  const localDate = parseLocalDate(String(value).split('T')[0]);
  if (!localDate) return null;
  const formatted = formatDate(localDate);
  return formatted === 'N/A' || formatted === 'Invalid Date' ? null : formatted;
};

const getExpiryBadge = (alert) => {
  if (alert.bucket === 'no_data') return <StatusBadge status="warning" label="No expiry date" />;
  const label = formatExpiryUrgencyLabel(alert.days_until_expiry);
  if (alert.bucket === 'expired') {
    return <StatusBadge status="expired" label={label || 'Expired'} />;
  }
  return <StatusBadge status="expiring_soon" label={label || undefined} />;
};

const getPassTypeBadgeStatus = (passType) => {
  if (passType === 'ep') return 'in_progress';
  if (passType === 's_pass') return 'pending';
  return 'assigned';
};

// HR scan order: who → what document → how urgent.
// Identity gets the full card width; status metadata lives in a
// border-t footer with pass type left and expiry+date right-aligned
// as one column — no flex-wrap, no staggered badge baselines.
const ComplianceAlertCard = ({ alert, showPassType, typeKey }) => {
  const formatted = formatExpiryDate(alert.expiry_date);
  const hasPassType = showPassType && alert.pass_type_label;
  const workPassHref = typeKey === 'work_pass' && alert.staff_id
    ? `/admin/employee-lifecycle?tab=work-pass&staff_id=${encodeURIComponent(alert.staff_id)}`
    : '';
  const Wrapper = workPassHref ? Link : 'div';
  const wrapperProps = workPassHref
    ? {
        to: workPassHref,
        className: 'block rounded-xl border border-gray-200 p-4 space-y-3 hover:border-red-300 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500',
      }
    : { className: 'rounded-xl border border-gray-200 p-4 space-y-3' };

  return (
    <Wrapper {...wrapperProps}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 break-words">{alert.employee_name}</p>
        <p className="text-xs text-gray-500 mt-0.5 truncate">
          {alert.staff_id}{alert.designation ? ` · ${alert.designation}` : ''}
        </p>
        {alert.cert_name && (
          <span className="inline-block max-w-full mt-2 rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200 break-words">
            {alert.cert_name}
          </span>
        )}
      </div>

      <div className={`flex items-start gap-3 border-t border-gray-100 pt-3 ${hasPassType ? 'justify-between' : 'justify-end'}`}>
        {hasPassType && (
          <StatusBadge
            status={getPassTypeBadgeStatus(alert.pass_type)}
            label={alert.pass_type_label}
          />
        )}
        <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
          {getExpiryBadge(alert)}
          {formatted && (
            <span className="text-[11px] leading-tight text-gray-500 tabular-nums">
              {formatted}
            </span>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

// ── Bucket cards (reusable for all tabs) ─────────────────────────────

const BucketCards = ({ data, onActionClick, onWatchClick }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6" title="Total tracked">
      <div className="flex items-start">
        <div className="w-9 h-9 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
          <Users className="w-4 h-4 sm:w-6 sm:h-6 text-blue-800" />
        </div>
        <div className="ml-2 sm:ml-4 min-w-0">
          <p className="text-lg sm:text-2xl font-bold text-gray-900">{data.total_tracked}</p>
          <p className="text-xs sm:text-sm text-gray-600 truncate">Tracked</p>
        </div>
      </div>
    </div>

    <button
      onClick={onActionClick}
      title="Expired or expiring within 30 days"
      className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 w-full text-left transition-all cursor-pointer hover:ring-2 hover:ring-red-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
    >
      <div className="flex items-start">
        <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${data.action_required > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
          <ShieldAlert className={`w-4 h-4 sm:w-6 sm:h-6 ${data.action_required > 0 ? 'text-red-600' : 'text-green-600'}`} />
        </div>
        <div className="ml-2 sm:ml-4 min-w-0">
          <p className={`text-lg sm:text-2xl font-bold ${data.action_required > 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {data.action_required}
          </p>
          <p className="text-xs sm:text-sm text-gray-600 truncate">Action Required</p>
          <div className="mt-1 space-y-0.5">
            <p className="text-[11px] sm:text-xs text-red-600 truncate">{data.bucket_counts.expired} expired</p>
            <p className="text-[11px] sm:text-xs text-orange-600 truncate">{data.bucket_counts.within_30} within 30 days</p>
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-end gap-1 text-[11px] sm:text-xs text-red-600 font-medium">
        <span className="truncate">View details</span> <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
      </div>
    </button>

    <button
      onClick={onWatchClick}
      title="Expiring within 31–90 days"
      className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 w-full text-left transition-all cursor-pointer hover:ring-2 hover:ring-orange-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
    >
      <div className="flex items-start">
        <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${data.watch_list > 0 ? 'bg-orange-100' : 'bg-green-100'}`}>
          <AlertTriangle className={`w-4 h-4 sm:w-6 sm:h-6 ${data.watch_list > 0 ? 'text-orange-600' : 'text-green-600'}`} />
        </div>
        <div className="ml-2 sm:ml-4 min-w-0">
          <p className={`text-lg sm:text-2xl font-bold ${data.watch_list > 0 ? 'text-orange-600' : 'text-gray-900'}`}>
            {data.watch_list}
          </p>
          <p className="text-xs sm:text-sm text-gray-600 truncate">Watch List</p>
          <div className="mt-1 space-y-0.5">
            <p className="text-[11px] sm:text-xs text-orange-600 truncate">{data.bucket_counts.within_60} within 60 days</p>
            <p className="text-[11px] sm:text-xs text-yellow-600 truncate">{data.bucket_counts.within_90} within 90 days</p>
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-end gap-1 text-[11px] sm:text-xs text-orange-600 font-medium">
        <span className="truncate">View details</span> <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
      </div>
    </button>

    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6" title="More than 90 days validity remaining">
      <div className="flex items-start">
        <div className="w-9 h-9 sm:w-12 sm:h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
          <ShieldCheck className="w-4 h-4 sm:w-6 sm:h-6 text-emerald-600" />
        </div>
        <div className="ml-2 sm:ml-4 min-w-0">
          <p className="text-lg sm:text-2xl font-bold text-gray-900">{data.bucket_counts.valid}</p>
          <p className="text-xs sm:text-sm text-gray-600 truncate">Valid (&gt;90 days)</p>
          {data.bucket_counts.no_data > 0 && (
            <p className="text-[11px] sm:text-xs text-gray-500 mt-1 truncate">{data.bucket_counts.no_data} missing expiry date</p>
          )}
        </div>
      </div>
    </div>
  </div>
);

// ── Stacked bar chart (work pass only) ────────────────────────────────

const PassTypeDistribution = ({ byPassType }) => {
  if (!byPassType || Object.keys(byPassType).length === 0) return null;
  return (
    <div className="mt-6 bg-gray-50 rounded-xl border p-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Expiry Distribution by Pass Type</h3>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] sm:text-xs font-medium">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-500 inline-block" /> Expired</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-orange-500 inline-block" /> &le;30d</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400 inline-block" /> 31-60d</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-yellow-300 inline-block" /> 61-90d</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" /> &gt;90d</span>
        </div>
      </div>
      <div className="space-y-3">
        {Object.entries(byPassType).map(([key, val]) => {
          const total = val.expired + val.within_30 + val.within_60 + val.within_90 + val.valid + (val.no_data || 0);
          if (total === 0) return null;
          const segments = [
            { count: val.expired, color: 'bg-red-500', label: 'Expired' },
            { count: val.within_30, color: 'bg-orange-500', label: '≤30d' },
            { count: val.within_60, color: 'bg-amber-400', label: '31-60d' },
            { count: val.within_90, color: 'bg-yellow-300', label: '61-90d' },
            { count: val.valid, color: 'bg-emerald-500', label: '>90d' },
          ];
          return (
            <div key={key}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-700">{val.label || key}</span>
                <span className="text-xs text-gray-500">{total} total</span>
              </div>
              <div className="flex w-full h-5 rounded-md overflow-hidden bg-gray-200" title={segments.map(s => `${s.label}: ${s.count}`).join(' | ')}>
                {segments.map((seg, idx) => {
                  const pct = total > 0 ? (seg.count / total) * 100 : 0;
                  if (pct === 0) return null;
                  return (
                    <div key={idx} className={`${seg.color} transition-all duration-500 relative group`} style={{ width: `${pct}%` }}>
                      {pct >= 10 && (
                        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white drop-shadow-sm">{seg.count}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Detail modal ──────────────────────────────────────────────────────

const ComplianceDetailModal = ({ typeKey, filter, data, onClose }) => {
  if (!data) return null;

  const isActionRequired = filter === 'action_required';
  const alerts = data.alerts || [];
  const expiredRows = alerts.filter(a => a.bucket === 'expired');
  const within30Rows = alerts.filter(a => a.bucket === 'within_30');
  const noDataRows = alerts.filter(a => a.bucket === 'no_data');
  const within60Rows = alerts.filter(a => a.bucket === 'within_60');
  const within90Rows = alerts.filter(a => a.bucket === 'within_90');

  const tabCfg = TAB_CONFIG.find(t => t.key === typeKey);
  const typeLabel = tabCfg?.label || typeKey;

  const groups = isActionRequired
    ? [
        { label: 'Expired', rows: expiredRows },
        { label: 'Expiring within 30 days', rows: within30Rows },
        { label: 'Missing Expiry Date', rows: noDataRows },
      ]
    : [
        { label: '31–60 days remaining', rows: within60Rows },
        { label: '61–90 days remaining', rows: within90Rows },
      ];

  const totalCount = groups.reduce((sum, g) => sum + g.rows.length, 0);

  const showPassType = typeKey === 'work_pass' || typeKey === 'passport';

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container modal-container-lg" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <div>
              <h3 className="modal-title flex items-center gap-2">
                {isActionRequired
                  ? <><ShieldAlert className="w-5 h-5 text-red-600" /> {typeLabel} — Action Required</>
                  : <><AlertTriangle className="w-5 h-5 text-orange-500" /> {typeLabel} — Watch List</>
                }
                <CountBadge count={totalCount} theme="admin" size="md" />
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {isActionRequired
                  ? `Expired or expiring within 30 days`
                  : `Expiring within 31–90 days — plan renewals now`
                }
              </p>
            </div>
            <button onClick={onClose} className="modal-close-admin" aria-label="Close modal">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="modal-content overflow-y-auto">
            {totalCount === 0 ? (
              <div className="py-10 text-center">
                <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-700">No employees in this category.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {groups.map((group) => {
                  if (group.rows.length === 0) return null;
                  return (
                    <div key={group.label} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                      <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{group.label}</span>
                        <CountBadge count={group.rows.length} theme="admin" />
                      </div>
                      <div className="space-y-3 p-3">
                        {group.rows.map((alert, idx) => (
                          <ComplianceAlertCard
                            key={idx}
                            alert={alert}
                            showPassType={showPassType}
                            typeKey={typeKey}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button onClick={onClose} className="btn btn-outline w-full">Close</button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

// ── Empty state ───────────────────────────────────────────────────────

const EmptyState = ({ label }) => (
  <div className="py-10 text-center">
    <CheckCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
    <p className="text-sm font-medium text-gray-500">No {label.toLowerCase()} records tracked.</p>
    <p className="text-xs text-gray-400 mt-1">Employees with this document type will appear here once data is available.</p>
  </div>
);

// ── Tab subtitles ─────────────────────────────────────────────────────

const TAB_SUBTITLES = {
  work_pass: 'EP / S Pass / Work Permit expiry compliance tracker',
  passport: 'Passport expiry tracking for all foreign workers',
  security_bond: 'Security bond expiry for Work Permit holders (MOM requirement)',
  safety_certs: 'All renewable safety/trade certs — CSOC (Apply WSH in Construction Sites), WAH, First Aid, CoreTrade, etc.',
};

// ── Main component ────────────────────────────────────────────────────

const ComplianceExpirySection = ({ complianceExpiryData, complianceExpiryLoading }) => {
  const [activeTab, setActiveTab] = useState('work_pass');
  const [modalState, setModalState] = useState(null); // { typeKey, filter }

  const closeModal = () => setModalState(null);

  // Escape key handler for modal
  React.useEffect(() => {
    if (!modalState) return;
    const handleEscape = (e) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [modalState]);

  const activeData = complianceExpiryData?.[activeTab];

  return (
    <>
      <section className="space-y-6" aria-label="Compliance Expiry Tracker">
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          {/* Header */}
          <div className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 min-w-0">
              <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0" />
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Compliance Expiry Tracker</h2>
            </div>
            <p className="text-xs sm:text-sm text-gray-600">Consolidated view of all compliance-critical expiry dates</p>
          </div>

          {complianceExpiryLoading ? (
            <div className="space-y-6">
              <div className="flex gap-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-9 w-28 bg-gray-200 rounded-full animate-pulse" />
                ))}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
                    <div className="flex items-center">
                      <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0" />
                      <div className="ml-2 sm:ml-4 space-y-2 min-w-0 flex-1">
                        <div className="h-6 w-12 bg-gray-200 rounded" />
                        <div className="h-4 w-24 bg-gray-200 rounded" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : complianceExpiryData ? (
            <>
              {/* ── Tab Navigation ───────────────────────────────── */}
              <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-6">
                {TAB_CONFIG.map(({ key, label, icon: Icon }) => {
                  const d = complianceExpiryData[key];
                  const actionCount = d?.action_required || 0;
                  return (
                    <button
                      key={key}
                      onClick={() => setActiveTab(key)}
                      className={`tab-btn-admin ${activeTab === key ? 'active' : ''}`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                      {actionCount > 0 && <CountBadge count={actionCount} theme="admin" />}
                    </button>
                  );
                })}
              </div>

              {/* ── Tab subtitle ─────────────────────────────────── */}
              <p className="text-xs sm:text-sm text-gray-500 mb-4">{TAB_SUBTITLES[activeTab]}</p>

              {/* ── Tab Content ──────────────────────────────────── */}
              {activeData && activeData.total_tracked > 0 ? (
                <>
                  <BucketCards
                    data={activeData}
                    onActionClick={() => setModalState({ typeKey: activeTab, filter: 'action_required' })}
                    onWatchClick={() => setModalState({ typeKey: activeTab, filter: 'watch_list' })}
                  />

                  {/* Stacked bar chart — work pass only */}
                  {activeTab === 'work_pass' && activeData.by_pass_type && (
                    <PassTypeDistribution byPassType={activeData.by_pass_type} />
                  )}
                </>
              ) : activeData ? (
                <EmptyState label={TAB_CONFIG.find(t => t.key === activeTab)?.label || activeTab} />
              ) : null}
            </>
          ) : (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
                <p className="text-sm font-medium text-yellow-800">
                  Unable to load compliance expiry data. Please check confidential database access.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Detail Modal ──────────────────────────────────────────── */}
      {modalState && complianceExpiryData?.[modalState.typeKey] && (
        <ComplianceDetailModal
          typeKey={modalState.typeKey}
          filter={modalState.filter}
          data={complianceExpiryData[modalState.typeKey]}
          onClose={closeModal}
        />
      )}
    </>
  );
};

export default ComplianceExpirySection;
